import React, { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useMatchMedia } from '~/src/hooks/use-match-media.hook';
import { useFetchGenerateAgentCode } from '~/src/repositories/user/hooks/use-fetch-generate-agent-code';
import { useOrganizationsIsLoading } from '~/src/hooks/use-organizations-is-loading.hook';
import { MediaWidthEnum } from '~/src/models/enums/media-width.enum';
import { NoSSR } from '~/src/helpers/noSSR.helper';
import { recommendProductBannerContent } from '~/src/components/blocks/agency-products/recommend-product-banner/recommend-product-banner.constants';
import { MainButtonVisualEnum } from '~/src/components/form-components/main-button/main-button-visual.enum';
import { onSendButtonClickAnalytics } from '~/src/components/blocks/agency-products/recommend-product-banner/recommend-product-banner.analytics';
import { generateRefLink } from '~/src/components/blocks/agency-products/recommend-product-banner/recommend-product-banner.utils';
import { KnowledgeBaseBannersSkeleton } from '~/src/components/skeletons/knowledge-base-banners-skeleton/knowledge-base-banners-skeleton';
import { selectAgentId, selectUserProfileIsUpdating } from '~/src/redux/user/user.selectors';
import { COLORS } from '~/src/styles/tokens/colors';
import { IMAGE_QUALITY } from '~/src/models/constants/image-quality.constants';
import { ABOUT_PRODUCTS, CLIENTS_REQUEST } from '~/src/params';
import { SvgArrowIcon } from '~/src/components/svg-components/arrow-icon';
import { NavLink } from '~/src/components/common-components/nav-link/nav-link';
import { KnowledgeBaseSkeletonTypeEnum } from '~/src/components/skeletons/knowledge-base-banners-skeleton/knowledge-base-banners-skeleton.types';
import type { ReferralProductInterface } from '~/src/models/types/referral-product.types';
import * as Styled from './about-products-banner.styles';

interface AboutProductsBannerProps {
    productData: ReferralProductInterface;
    isAuth: boolean;
    isAuthFinished: boolean;
    isConnectedStatus: boolean;
}

export const AboutProductsBanner: FC<AboutProductsBannerProps> = ({
    productData,
    isAuth,
    isAuthFinished,
    isConnectedStatus,
}) => {
    const router = useRouter();
    const dispatch = useDispatch();
    const userProfileIsUpdating = useSelector(selectUserProfileIsUpdating);
    const { bannerGradient, productName, bannerSubText, bannerImage, leadCreation, referralUrl } = productData;
    const [isTabletWide] = useMatchMedia(MediaWidthEnum.tabletWide, { withoutSSR: true });
    const organizationsIsLoading = useOrganizationsIsLoading();
    const [widthContentContainer, setWidthContentContainer] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const agentId = useSelector(selectAgentId);
    const { refetch } = useFetchGenerateAgentCode();

    const widthImageAndIndent = 432;

    useEffect(() => {
        if (containerRef && containerRef.current) {
            setTimeout(() => {
                const width = containerRef.current?.clientWidth;
                width && setWidthContentContainer(width);
            }, 0);
        }
    }, [containerRef]);

    const handleCreateNewLead = async () => {
        onSendButtonClickAnalytics('novyi_lid');
        await router.push({ pathname: `${CLIENTS_REQUEST}` });
    };

    const getReferralLink = async (event: SyntheticEvent<HTMLElement, Event>) => {
        event.stopPropagation();
        event.preventDefault();

        onSendButtonClickAnalytics('referalnaya_ssylka');

        if (agentId && referralUrl) {
            generateRefLink(`${referralUrl}${agentId}`, dispatch);
        } else {
            await refetch();
        }
    };

    if (organizationsIsLoading || userProfileIsUpdating) {
        return (
            <Styled.AboutProductBannerContainer
                bannerGradient={bannerGradient || COLORS.gradientMintBlue}
                ref={containerRef}
            >
                <KnowledgeBaseBannersSkeleton type={KnowledgeBaseSkeletonTypeEnum.UPPER_BANNER} />
            </Styled.AboutProductBannerContainer>
        );
    }

    const handleGoBack = () => {
        router.push({ pathname: `${ABOUT_PRODUCTS}` }).then();
    };

    return (
        <Styled.AboutProductBannerContainer
            bannerGradient={bannerGradient || COLORS.gradientMintBlue}
            ref={containerRef}
        >
            <NoSSR>
                {isTabletWide && (
                    <Styled.SectionContainerGoBack>
                        <Styled.LinkWrapperGoBack>
                            <Styled.SvgWrapper onClick={handleGoBack}>
                                <SvgArrowIcon />
                            </Styled.SvgWrapper>
                            <NavLink
                                linkColor={COLORS.textLightPrimaryLink}
                                href={ABOUT_PRODUCTS}
                                textDecoration={'underline'}
                            >
                                Назад
                            </NavLink>
                        </Styled.LinkWrapperGoBack>
                    </Styled.SectionContainerGoBack>
                )}
            </NoSSR>
            <Styled.AboutProductBannerContent width={widthContentContainer - widthImageAndIndent}>
                {productName && <Styled.AboutProductBannerTitle>{productName}</Styled.AboutProductBannerTitle>}
                <Styled.BottomContainer>
                    {bannerSubText && <Styled.SubText>{bannerSubText}</Styled.SubText>}
                    {isAuth && isAuthFinished && isConnectedStatus && leadCreation === 'true' && (
                        <Styled.ButtonGroup>
                            <Styled.NewLeadButton
                                onClick={handleCreateNewLead}
                                textForSSR={recommendProductBannerContent.buttonText.newLead}
                                type={'button'}
                                typeVisual={MainButtonVisualEnum.primary}
                            >
                                {recommendProductBannerContent.buttonText.newLead}
                            </Styled.NewLeadButton>
                            {!!referralUrl?.length && (
                                <Styled.ReferralLinkButton
                                    onClick={(e) => getReferralLink(e)}
                                    textForSSR={recommendProductBannerContent.buttonText.referralLink}
                                    type={'button'}
                                    typeVisual={MainButtonVisualEnum.alwaysWhite}
                                >
                                    {recommendProductBannerContent.buttonText.referralLink}
                                </Styled.ReferralLinkButton>
                            )}
                        </Styled.ButtonGroup>
                    )}
                </Styled.BottomContainer>
            </Styled.AboutProductBannerContent>
            <NoSSR>
                {!isTabletWide && bannerImage && (
                    <Styled.ImageContainer>
                        <Styled.BannerImage
                            width={300}
                            height={200}
                            alt={'Логотип продукта МТС'}
                            src={bannerImage}
                            priority={true}
                            quality={IMAGE_QUALITY.HIGH}
                            unoptimized
                        />
                    </Styled.ImageContainer>
                )}
            </NoSSR>
        </Styled.AboutProductBannerContainer>
    );
};
